<template>
  <div id="app-root">
    <!-- 浮动光晕背景 -->
    <div class="bg-orbs">
      <div class="orb"></div>
      <div class="orb"></div>
      <div class="orb"></div>
    </div>

    <!-- 滚动进度条 -->
    <div class="scroll-progress" :style="{ width: scrollProgress + '%' }"></div>

    <!-- 返回顶部 -->
    <button class="back-to-top" :class="{ visible: showBackTop }" @click="scrollToTop" title="Back to top">↑</button>

    <header class="site-header" :class="{ scrolled: isScrolled, 'menu-open': menuOpen }">
      <div class="header-inner">
        <router-link to="/" class="logo">
          <img src="/images/logo.png" alt="CHARMRAY" class="logo-img" />
          <span class="logo-text" v-if="locale === 'zh'">创瑞激光</span>
          <span class="logo-text" v-else>CHARMRAY</span>
        </router-link>
        <nav class="main-nav">
          <router-link to="/" exact-active-class="active">{{ $t('nav.home') }}</router-link>
          <router-link to="/equipments" active-class="active">{{ $t('nav.equipments') }}</router-link>
          <router-link to="/products" active-class="active">{{ $t('nav.products') }}</router-link>
          <router-link to="/factory" active-class="active">{{ $t('nav.factory') }}</router-link>
          <router-link to="/projects" active-class="active">{{ $t('nav.projects') }}</router-link>
          <router-link to="/news" active-class="active">{{ $t('nav.news') }}</router-link>
          <router-link to="/about" active-class="active">{{ $t('nav.about') }}</router-link>
        </nav>
        <div class="header-actions">
          <button class="search-btn" @click="searchOpen = true" :title="locale === 'zh' ? '站内搜索' : 'Search'">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          </button>
          <div class="theme-switcher">
            <button
              v-for="t in themeList"
              :key="t.key"
              :class="{ active: currentTheme === t.key }"
              :title="locale === 'zh' ? t.name.zh : t.name.en"
              :style="{ background: t.bg }"
              @click="setTheme(t.key)"
              class="theme-dot"
            ></button>
          </div>
          <div class="lang-switch">
            <button
              :class="{ active: locale === 'zh' }"
              @click="switchLang('zh')"
            >中</button>
            <span class="lang-divider">|</span>
            <button
              :class="{ active: locale === 'en' }"
              @click="switchLang('en')"
            >EN</button>
          </div>
          <button class="menu-toggle" @click="menuOpen = !menuOpen" aria-label="Toggle menu">
            <span></span><span></span><span></span>
          </button>
        </div>
      </div>
    </header>

    <!-- 移动端菜单遮罩层 -->
    <div class="mobile-menu-overlay" :class="{ open: menuOpen }" @click="menuOpen = false">
      <nav class="mobile-menu-panel" @click.stop>
        <button class="mobile-menu-link mobile-search-link" @click="searchOpen = true; menuOpen = false">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          {{ locale === 'zh' ? '站内搜索' : 'Search' }}
        </button>
        <router-link to="/" exact-active-class="active" class="mobile-menu-link" @click="menuOpen = false">{{ $t('nav.home') }}</router-link>
        <router-link to="/equipments" active-class="active" class="mobile-menu-link" @click="menuOpen = false">{{ $t('nav.equipments') }}</router-link>
        <router-link to="/products" active-class="active" class="mobile-menu-link" @click="menuOpen = false">{{ $t('nav.products') }}</router-link>
        <router-link to="/factory" active-class="active" class="mobile-menu-link" @click="menuOpen = false">{{ $t('nav.factory') }}</router-link>
        <router-link to="/projects" active-class="active" class="mobile-menu-link" @click="menuOpen = false">{{ $t('nav.projects') }}</router-link>
        <router-link to="/news" active-class="active" class="mobile-menu-link" @click="menuOpen = false">{{ $t('nav.news') }}</router-link>
        <router-link to="/about" active-class="active" class="mobile-menu-link" @click="menuOpen = false">{{ $t('nav.about') }}</router-link>
        <div class="mobile-theme-section">
          <span class="mobile-theme-label">{{ locale === 'zh' ? '切换主题' : 'Theme' }}</span>
          <div class="mobile-theme-dots">
            <button
              v-for="t in themeList"
              :key="t.key"
              :class="{ active: currentTheme === t.key }"
              :style="{ background: t.bg }"
              @click="setTheme(t.key); menuOpen = false"
              class="mobile-theme-dot"
            ></button>
          </div>
        </div>
      </nav>
    </div>

    <main class="main-content">
      <router-view v-slot="{ Component }">
        <transition name="page-fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <footer class="site-footer">
      <div class="footer-inner">
        <div class="footer-grid">
          <div class="footer-col">
            <h4>{{ locale === 'zh' ? '创瑞激光' : 'CHARMRAY' }}</h4>
            <p>{{ locale === 'zh' ? '工业级金属3D打印解决方案' : 'Industrial-Grade Metal 3D Printing Solutions' }}</p>
            <p class="footer-tagline">{{ locale === 'zh' ? '专注于装备 - 材料 - 工艺 - 结构功能一体化创新设计与高质量制造' : 'Focused on integrated innovative design and high-quality manufacturing of equipment, materials, processes, and structural functions.' }}</p>
          </div>
          <div class="footer-col">
            <h4>{{ $t('footer.quickLinks') }}</h4>
            <router-link to="/equipments">{{ $t('nav.equipments') }}</router-link>
            <router-link to="/products">{{ $t('nav.products') }}</router-link>
            <router-link to="/factory">{{ $t('nav.factory') }}</router-link>
            <router-link to="/projects">{{ $t('nav.projects') }}</router-link>
          </div>
          <div class="footer-col">
            <h4>{{ $t('footer.company') }}</h4>
            <router-link to="/about">{{ $t('nav.about') }}</router-link>
            <router-link to="/news">{{ $t('nav.news') }}</router-link>
          </div>
          <div class="footer-col">
            <h4>{{ $t('footer.contact') }}</h4>
            <p>{{ locale === 'zh' ? '山东烟台 & 陕西西安' : 'Yantai, Shandong & Xi\'an, Shaanxi' }}</p>
            <p>{{ locale === 'zh' ? '中国' : 'China' }}</p>
            <router-link to="/about" class="btn-contact">{{ $t('footer.getInTouch') }}</router-link>
          </div>
        </div>
        <div class="footer-bottom">
          <p>&copy; {{ currentYear }} {{ $t('footer.rights') }}</p>
        </div>
      </div>
    </footer>

    <SearchModal :visible="searchOpen" @close="searchOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTheme } from './composables/useTheme'
import SearchModal from './components/SearchModal.vue'

const { locale } = useI18n()
const { currentTheme, themeList, setTheme } = useTheme()
const router = useRouter()
const menuOpen = ref(false)
const searchOpen = ref(false)
const isScrolled = ref(false)
const scrollProgress = ref(0)
const showBackTop = ref(false)
const currentYear = computed(() => new Date().getFullYear())

function switchLang(lang) {
  locale.value = lang
  localStorage.setItem('lang', lang)
}

function handleScroll() {
  const scrollTop = window.scrollY
  const docHeight = document.documentElement.scrollHeight - window.innerHeight
  isScrolled.value = scrollTop > 50
  scrollProgress.value = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
  showBackTop.value = scrollTop > 500
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function handleKeydown(e) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    searchOpen.value = !searchOpen.value
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('keydown', handleKeydown)
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('keydown', handleKeydown)
})

router.afterEach(() => {
  menuOpen.value = false
  window.scrollTo({ top: 0, behavior: 'instant' })
})
</script>

<style scoped>
#app-root {
  position: relative;
}
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(var(--color-bg-rgb), 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--color-border);
  transition: all 0.3s ease;
}
.site-header.scrolled {
  background: rgba(var(--color-bg-rgb), 0.96);
  box-shadow: var(--color-header-shadow);
}
.header-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 10px;
}
.logo-img {
  height: 40px;
  width: auto;
}
.logo-text {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-heading);
  letter-spacing: 1px;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}
.search-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: var(--color-nav-hover-bg);
  color: var(--color-nav-text);
  cursor: pointer;
  transition: all 0.25s;
  padding: 0;
}
.search-btn:hover {
  color: var(--color-primary);
  background: rgba(0,212,255,0.1);
}
.theme-switcher {
  display: flex;
  align-items: center;
  gap: 6px;
}
.theme-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  cursor: pointer;
  transition: all 0.25s ease;
  padding: 0;
  outline: none;
}
.theme-dot:hover {
  border-color: var(--color-primary);
  transform: scale(1.25);
}
.theme-dot.active {
  border-color: var(--color-primary);
  box-shadow: 0 0 6px var(--color-primary);
  transform: scale(1.2);
}
.lang-switch {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
}
.lang-switch button {
  background: none;
  border: none;
  color: var(--color-nav-text);
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 4px 6px;
  border-radius: 4px;
  transition: all 0.2s;
}
.lang-switch button:hover {
  color: var(--color-text);
}
.lang-switch button.active {
  color: var(--color-primary);
  font-weight: 700;
}
.lang-divider {
  color: var(--color-border);
}
.main-nav {
  display: flex;
  gap: 4px;
}
.main-nav a {
  color: var(--color-nav-text);
  text-decoration: none;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 400;
  transition: all 0.25s ease;
  position: relative;
}
.main-nav a::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: var(--color-gradient);
  border-radius: 2px;
  transition: width 0.3s ease;
}
.main-nav a:hover::after,
.main-nav a.active::after {
  width: 60%;
}
.main-nav a:hover,
.main-nav a.active {
  color: var(--color-primary);
  background: var(--color-nav-hover-bg);
}
.menu-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}
.menu-toggle span {
  display: block;
  width: 24px;
  height: 2px;
  background: var(--color-heading);
  border-radius: 2px;
  transition: all 0.3s ease;
}
.main-content {
  min-height: calc(100vh - 400px);
  padding-top: 72px;
  position: relative;
  z-index: 1;
}
.site-footer {
  background: var(--color-footer-bg);
  border-top: 1px solid var(--color-border);
  padding: 60px 0 30px;
  position: relative;
  z-index: 1;
}
.footer-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
}
.footer-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  margin-bottom: 40px;
}
.footer-col h4 {
  color: var(--color-heading);
  font-size: 1rem;
  margin-bottom: 16px;
  font-weight: 600;
}
.footer-col p,
.footer-col a {
  display: block;
  color: var(--color-text-muted);
  text-decoration: none;
  font-size: 0.88rem;
  margin-bottom: 8px;
  transition: color 0.2s;
}
.footer-col a:hover {
  color: var(--color-primary);
}
.footer-tagline {
  font-style: italic;
  color: var(--color-text-muted) !important;
  opacity: 0.65;
}
.btn-contact {
  display: inline-block;
  margin-top: 12px;
  padding: 10px 24px;
  border: 1px solid var(--color-primary);
  border-radius: 6px;
  color: var(--color-primary) !important;
  font-weight: 500;
  transition: all 0.3s;
}
.btn-contact:hover {
  background: var(--color-primary);
  color: var(--color-bg) !important;
}
.footer-bottom {
  border-top: 1px solid var(--color-border);
  padding-top: 24px;
  text-align: center;
  color: var(--color-text-muted);
  opacity: 0.6;
  font-size: 0.82rem;
}

/* Enhanced page transition */
.page-fade-enter-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.page-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.page-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 1024px) {
  .footer-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 768px) {
  .main-nav {
    display: none !important;
  }
  .menu-toggle {
    display: flex;
    flex-shrink: 0;
  }
  .menu-open .menu-toggle span:nth-child(1) {
    transform: rotate(45deg) translate(5px, 5px);
  }
  .menu-open .menu-toggle span:nth-child(2) {
    opacity: 0;
  }
  .menu-open .menu-toggle span:nth-child(3) {
    transform: rotate(-45deg) translate(5px, -5px);
  }
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
}
@media (max-width: 480px) {
  .header-inner { padding: 0 14px; }
  .logo-text { font-size: 0.9rem; }
  .logo-img { height: 32px; }
  .theme-switcher { display: none; }
  .footer-inner { padding: 0 14px; }
  .footer-grid { grid-template-columns: 1fr; gap: 24px; }
  .site-footer { padding: 40px 0 24px; }
}

/* ==================== Mobile Menu Overlay ==================== */
.mobile-menu-overlay {
  display: none;
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  padding: 72px 24px 24px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.mobile-menu-overlay.open {
  display: flex;
  flex-direction: column;
}
.mobile-menu-panel {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 420px;
  width: 100%;
  margin: 0 auto;
}
.mobile-menu-link {
  display: block;
  width: 100%;
  padding: 18px 24px;
  font-size: 1.3rem;
  font-weight: 500;
  color: #fff;
  text-decoration: none;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  transition: background 0.2s, color 0.2s;
}
.mobile-menu-link:last-child {
  border-bottom: none;
}
.mobile-search-link {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(0,212,255,0.08);
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 1.15rem;
  font-weight: 500;
  color: var(--color-primary);
}
.mobile-menu-link:hover,
.mobile-menu-link.active {
  color: var(--color-primary);
  background: rgba(255, 255, 255, 0.06);
}

/* Mobile theme section */
.mobile-theme-section {
  padding: 20px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  margin-top: 8px;
}
.mobile-theme-label {
  display: block;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.82rem;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.mobile-theme-dots {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.mobile-theme-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.25s ease;
  padding: 0;
  outline: none;
}
.mobile-theme-dot.active {
  border-color: var(--color-primary);
  box-shadow: 0 0 10px var(--color-primary);
  transform: scale(1.15);
}
</style>
